@let hasShiftUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasServiceUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Service);
@let hasProjectReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Project);
@let showShiftValidation =
  !!('portal-validation-without-assignments' | featureFlagPure | async);
@let project = project$ | async; @let label = label$ | async;
@let timelineWidthPixel = timelineWidthPixel$ | async;
@let interval = interval$ | async;
@if (
  {
    shiftRenderDatetime: shift | determineShiftRenderDatetime,
    shiftIdsToHighlight: shiftIdsToHighlight$ | async,
    areActivitiesVisible: areActivitiesVisible$ | async,
  };
  as shiftDetail
) {
  @let rowsActivities =
    shiftDetail.areActivitiesVisible
      ? ((shiftActivitiesRows$ | async) || [] | rowsToActivities)
      : [];
  @let leftPixelPosition =
    shiftDetail.shiftRenderDatetime.startDatetime.date
      | ganttDateToPositionPx
        : {
            fromRightSide: false,
            timelineWidthPixel: timelineWidthPixel,
            interval: interval,
          };
  @let rightPixelPosition =
    shiftDetail.shiftRenderDatetime.endDatetime.date
      | ganttDateToPositionPx
        : {
            fromRightSide: true,
            timelineWidthPixel: timelineWidthPixel,
            interval: interval,
          };
  @let calculatedShiftWidth =
    { left: leftPixelPosition, right: rightPixelPosition || 0 }
      | timeDifferenceWidthPx
      | async;
  @let isMinimalHeight = staffLayoutStyle === StaffLayoutStyle.Minimal;
  @let showActivities = shiftDetail.areActivitiesVisible || false;
  @let isShiftHighlighted =
    !!shiftDetail.shiftIdsToHighlight?.includes(shift.id);
  @let isShiftVisible =
    timelineShiftDragAndDropDirectiveHelperService.isShiftVisible$ | async;
  @defer (on hover) {
    <wilson-assignment-shift-card
      wilsonUnassignedShiftsWithoutActivitiesDragHandler
      [hasServiceUpdatePermissions]="!!hasServiceUpdatePermissions"
      [hasShiftUpdatePermissions]="!!hasShiftUpdatePermissions"
      [class]="IGNORE_ON_CLICK_CLASS_NAME"
      [shift]="shift"
      [shiftId]="shift.id"
      [shiftState]="shift.state"
      [publicationStatus]="shift.publicationStatus"
      [isShiftCompleted]="shift.operationalStatus === OperationStatus.Completed"
      [isShiftStarted]="shift.operationalStatus === OperationStatus.Ongoing"
      [shiftCategoryId]="shift.shiftCategoryId"
      [shiftName]="shift.name"
      [showShiftValidation]="showShiftValidation"
      [isShiftDraggable]="true"
      [isMoveMode]="isMoveMode"
      [isAssignmentMode]="isAssignmentMode"
      [isTooltipVisible]="
        timelineShiftDragAndDropDirectiveHelperService.isTooltipVisible$ | async
      "
      [isShiftVisible]="isShiftVisible"
      [shiftStartDetail]="shiftDetail.shiftRenderDatetime.startDatetime"
      [shiftEndDetail]="shiftDetail.shiftRenderDatetime.endDatetime"
      [plannedShiftStartDateTime]="shift.startDatetime"
      [plannedShiftEndDateTime]="shift.endDatetime"
      [plannedStartLocation]="shift.startLocation || null"
      [plannedEndLocation]="shift.endLocation || null"
      [reportedStartLocation]="shift.reportedStartLocation || null"
      [reportedEndLocation]="shift.reportedEndLocation || null"
      [leftPixelPosition]="leftPixelPosition"
      [rightPixelPosition]="rightPixelPosition"
      [isHighlighted]="isShiftHighlighted"
      [frameLength]="timelineWidthPixel"
      [timeframe]="interval"
      [showActivities]="showActivities"
      [isActivityDraggable]="false"
      [rowsActivities]="rowsActivities"
      [isMinimalHeight]="isMinimalHeight"
      [calculatedShiftWidth]="calculatedShiftWidth"
      [project]="project"
      [label]="label"
      [shiftHeight]="shiftHeight"
      [loadIconsImmediately]="true"
      (selectActivity)="openActivityDrawer($event)"
      (startActivityDrag)="
        ganttActivityDragHandlerHelperService.startDrag($event)
      "
      (dragging)="
        ganttActivityDragHandlerHelperService.setDemarcatorRange($event)
      "
      (endActivityDrag)="ganttActivityDragHandlerHelperService.dragEnd()"
      (setSnappingForResize)="
        ganttActivityResizeHandlerHelperService.setSnapping($event)
      "
      (fillActivityGapLeft)="
        ganttActivityResizeHandlerHelperService.fillActivityGapLeft($event)
      "
      (handleResizeLeft)="
        ganttActivityResizeHandlerHelperService.onMouseDown($event, false)
      "
      (fillActivityGapRight)="
        ganttActivityResizeHandlerHelperService.fillActivityGapRight($event)
      "
      (handleResizeRight)="
        ganttActivityResizeHandlerHelperService.onMouseDown($event, true)
      "
      [isSelectionEnabled]="true"
    ></wilson-assignment-shift-card>
  } @placeholder {
    <div class="preview-shift-container">
      <ng-template #activitiesContentTemplate>
        <ng-container
          *ngTemplateOutlet="
            deferredAssignmentShiftCardHelperService.getActivitiesContentTemplate();
            context: {
              showActivities: showActivities,
              calculatedShiftWidth:
                calculatedShiftWidth - SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX,
              isDragging: false,
              rowsActivities: rowsActivities,
              shiftStartDetail: shiftDetail.shiftRenderDatetime.startDatetime,
              shiftEndDetail: shiftDetail.shiftRenderDatetime.endDatetime,
              publicationStatus: shift.publicationStatus,
              isActivityDraggable: false,
              frameLength: timelineWidthPixel,
              timeframe: interval,
              userId: shift.userId,
              isMoveMode: isMoveMode,
              isMinimalHeight: isMinimalHeight,
            }
          "
        >
        </ng-container>
      </ng-template>
      <ng-container
        *ngTemplateOutlet="
          deferredAssignmentShiftCardHelperService.getPreviewTemplate();
          context: {
            shift: shift,
            isMinimalHeight: isMinimalHeight,
            showActivities: showActivities,
            leftPixelPosition: leftPixelPosition,
            calculatedShiftWidth: calculatedShiftWidth,
            shiftColorOption: shiftColorOption,
            label: label,
            project: project,
            shiftCategoryId: shift.shiftCategoryId,
            hasProjectReadPermission: hasProjectReadPermission,
            showShiftValidation: showShiftValidation,
            hasShiftBeenValidated:
              shiftCardTooltipStateService.hasShiftBeenValidated$ | async,
            isLoadingValidations:
              (shiftCardTooltipStateService.isLoadingValidationsForUser$
                | async) ||
              (shiftCardTooltipStateService.isShiftValidationLoading$ | async),
            isLoadingValidationsError:
              shiftCardTooltipStateService.isLoadingValidationsError$ | async,
            isShiftValid: shiftCardTooltipStateService.isShiftValid$ | async,
            isShiftCompleted:
              shift.operationalStatus === OperationStatus.Completed,
            isShiftStarted: shift.operationalStatus === OperationStatus.Ongoing,
            shiftStartDetail: shiftDetail.shiftRenderDatetime.startDatetime,
            shiftEndDetail: shiftDetail.shiftRenderDatetime.endDatetime,
            isHighlighted: isShiftHighlighted,
            isShiftVisible: isShiftVisible,
            shiftHeight: shiftHeight,
            loadIconsImmediately: loadIconsImmediately$ | async,
            activityDisplayTemplate: activitiesContentTemplate,
            numberOfInnerRows: 1,
            innerRowIndex: 0,
            areActivitiesOverlapping: rowsActivities.length > 1,
            isFiltered: false,
            isDragging: false,
            contentTemplate: null,
            isShiftDraggable: true,
            isShiftSelected: false,
          }
        "
      ></ng-container>
    </div>
  }
}
